import React, { useState, useEffect } from "react";
import api from "../../../services/api";

const isTauri = typeof window !== 'undefined' && (window.__TAURI__ || window.__TAURI_INTERNALS__);

const colors = {
  navy900: "#030711",
  navy800: "#060e1a",
  navy700: "#0a1628",
  navy600: "#0f1a2e",
  teal500: "#14b8a6",
  teal400: "#2dd4bf",
  teal300: "#5eead4",
  teal700: "#134e4a",
  gold400: "#fbbf24",
  white: "#ffffff",
  gray400: "#9ca3af",
  gray500: "#6b7280",
};

export default function FileBrowser() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCase, setSelectedCase] = useState(null);
  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [syncBasePath, setSyncBasePath] = useState(null);
  const [downloading, setDownloading] = useState({});
  const [syncStatus, setSyncStatus] = useState(null);
  const [precacheStatus, setPrecacheStatus] = useState(null);
  const [precaching, setPrecaching] = useState(false);

  useEffect(() => {
    fetchCases();
    if (isTauri) {
      getSyncPath();
      setAuthToken();
    }
  }, []);

  const setAuthToken = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const { invoke } = await import('@tauri-apps/api/core');
        await invoke('set_auth_token', { token });
      }
    } catch (err) {
      console.error("Failed to set auth token:", err);
    }
  };

  const getSyncPath = async () => {
    try {
      const { invoke } = await import('@tauri-apps/api/core');
      const path = await invoke('get_sync_base_path');
      setSyncBasePath(path);
    } catch (err) {
      console.error("Failed to get sync path:", err);
    }
  };

  const fetchCases = async () => {
    try {
      setLoading(true);
      const response = await api.get("/cases/?status=active");
      setCases(response.data || []);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch cases:", err);
      setError("Failed to load cases: " + (err.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  const openCase = async (c) => {
    setSelectedCase(c);
    setFilesLoading(true);
    try {
      const response = await api.get("/virgo/cases/" + c.id + "/documents");
      const docs = response.data?.documents || response.data || [];
      setFiles(docs);
    } catch (err) {
      console.error("Failed to fetch files:", err);
      setFiles([]);
    } finally {
      setFilesLoading(false);
    }
  };

  const goBack = () => {
    setSelectedCase(null);
    setFiles([]);
  };

  const getFileName = (f) => {
    return f.original_file_name || f.file_name || f.name || f.filename || "Unnamed file";
  };

  const getFileIcon = (filename) => {
    const ext = filename?.split(".").pop()?.toLowerCase() || "";
    if (["pdf"].includes(ext)) return "\u{1F4C4}";
    if (["doc", "docx"].includes(ext)) return "\u{1F4DD}";
    if (["xls", "xlsx"].includes(ext)) return "\u{1F4CA}";
    if (["jpg", "jpeg", "png", "gif"].includes(ext)) return "\u{1F5BC}";
    if (["mp4", "mov", "avi"].includes(ext)) return "\u{1F3AC}";
    return "\u{1F4CE}";
  };

  const openVirgoFolder = async () => {
    if (!isTauri) return;
    try {
      const { invoke } = await import('@tauri-apps/api/core');
      await invoke('open_virgo_folder');
    } catch (err) {
      console.error("Failed to open folder:", err);
    }
  };

  const syncCaseFolder = async (c) => {
    if (!isTauri) return;
    try {
      const { invoke } = await import('@tauri-apps/api/core');
      const caseName = c.case_name || c.name || c.title || "Case";
      const caseNumber = c.case_number || "000";
      await invoke('create_case_folder', {
        caseId: c.id,
        caseName: caseName,
        caseNumber: caseNumber,
      });
      await invoke('open_case_folder', { caseId: c.id });
    } catch (err) {
      console.error("Failed to sync case folder:", err);
    }
  };

  const downloadFile = async (doc) => {
    if (!isTauri || !selectedCase) return;

    const docId = doc.id;
    setDownloading(prev => ({ ...prev, [docId]: true }));

    try {
      const { invoke } = await import('@tauri-apps/api/core');
      const fileName = getFileName(doc);
      const caseName = selectedCase.case_name || selectedCase.name || selectedCase.title || "Case";
      const caseNumber = selectedCase.case_number || "000";

      const result = await invoke('download_document', {
        caseId: selectedCase.id,
        docId: docId,
        fileName: fileName,
        caseName: caseName,
        caseNumber: caseNumber,
      });

      if (result.success) {
        // Mark as downloaded
        setDownloading(prev => ({ ...prev, [docId]: 'done' }));
        setTimeout(() => {
          setDownloading(prev => ({ ...prev, [docId]: false }));
        }, 2000);
      } else {
        console.error("Download failed:", result.error);
        setDownloading(prev => ({ ...prev, [docId]: false }));
      }
    } catch (err) {
      console.error("Failed to download file:", err);
      setDownloading(prev => ({ ...prev, [docId]: false }));
    }
  };

  const downloadAllFiles = async () => {
    if (!isTauri || !selectedCase || files.length === 0) return;

    for (const doc of files) {
      await downloadFile(doc);
    }
  };

  const [uploading, setUploading] = useState(false);

  const fetchPrecacheStatus = async () => {
    if (!isTauri) return;
    try {
      const { invoke } = await import('@tauri-apps/api/core');
      const status = await invoke('get_precache_status');
      setPrecacheStatus(status);
    } catch (err) {
      console.error("Failed to get precache status:", err);
    }
  };

  const startPrecache = async () => {
    if (!isTauri || precaching) return;
    setPrecaching(true);
    try {
      const { invoke } = await import('@tauri-apps/api/core');
      const result = await invoke('start_precache', { daysAhead: 7 });
      setPrecacheStatus({
        ...precacheStatus,
        is_running: false,
        cached_cases: result.cases_cached,
        cached_files: result.files_cached,
        last_run: new Date().toISOString(),
        errors: result.errors,
      });
      if (result.success) {
        console.log(`Pre-cached ${result.files_cached} files from ${result.cases_cached} cases`);
      }
    } catch (err) {
      console.error("Failed to start precache:", err);
    } finally {
      setPrecaching(false);
    }
  };

  // Poll precache status while running
  useEffect(() => {
    if (!isTauri || !precaching) return;
    const interval = setInterval(fetchPrecacheStatus, 2000);
    return () => clearInterval(interval);
  }, [precaching]);

  const uploadFile = async () => {
    if (!isTauri || !selectedCase) return;

    setUploading(true);
    try {
      const { invoke } = await import('@tauri-apps/api/core');
      const result = await invoke('pick_and_upload_file', {
        caseId: selectedCase.id,
      });

      if (result.success) {
        // Refresh file list after upload
        await openCase(selectedCase);
      } else if (result.error && result.error !== "No file selected") {
        console.error("Upload failed:", result.error);
      }
    } catch (err) {
      console.error("Failed to upload file:", err);
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: "60px 20px", textAlign: "center" }}>
        <div style={{
          width: "40px",
          height: "40px",
          border: `3px solid ${colors.teal500}`,
          borderTopColor: "transparent",
          borderRadius: "50%",
          animation: "spin 1s linear infinite",
          margin: "0 auto 15px",
        }} />
        <div style={{color: colors.gray400}}>Loading cases...</div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); }}`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{padding: "40px 20px", textAlign: "center"}}>
        <div style={{
          backgroundColor: "rgba(239, 68, 68, 0.1)",
          border: "1px solid rgba(239, 68, 68, 0.3)",
          color: "#fca5a5",
          padding: "16px 20px",
          borderRadius: "8px",
          marginBottom: "20px",
          display: "inline-block",
        }}>
          {error}
        </div>
        <div>
          <button
            onClick={fetchCases}
            style={{
              padding: "10px 20px",
              background: `linear-gradient(135deg, ${colors.teal500} 0%, ${colors.teal400} 100%)`,
              color: colors.navy900,
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "600",
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (selectedCase) {
    return (
      <div style={{padding: "24px"}}>
        <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", flexWrap: "wrap", gap: "10px"}}>
          <button
            onClick={goBack}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 14px",
              backgroundColor: colors.navy700,
              border: `1px solid ${colors.teal700}`,
              color: colors.teal300,
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            Back to Cases
          </button>

          {isTauri && (
            <div style={{display: "flex", gap: "10px"}}>
              <button
                onClick={uploadFile}
                disabled={uploading}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 14px",
                  backgroundColor: colors.navy700,
                  border: `1px solid ${colors.teal500}`,
                  color: colors.teal300,
                  borderRadius: "6px",
                  cursor: uploading ? "not-allowed" : "pointer",
                  fontSize: "14px",
                  opacity: uploading ? 0.6 : 1,
                }}
              >
                {uploading ? "Uploading..." : "Upload File"}
              </button>
              {files.length > 0 && (
                <button
                  onClick={downloadAllFiles}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "8px 14px",
                    backgroundColor: colors.navy700,
                    border: `1px solid ${colors.gold400}`,
                    color: colors.gold400,
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "14px",
                  }}
                >
                  Download All
                </button>
              )}
              <button
                onClick={() => syncCaseFolder(selectedCase)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 14px",
                  background: `linear-gradient(135deg, ${colors.teal500} 0%, ${colors.teal400} 100%)`,
                  border: "none",
                  color: colors.navy900,
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "600",
                }}
              >
                Open Folder
              </button>
            </div>
          )}
        </div>

        <div style={{
          backgroundColor: colors.navy700,
          borderRadius: "12px",
          padding: "20px",
          border: `1px solid ${colors.teal700}`,
          marginBottom: "24px",
        }}>
          <h2 style={{
            color: colors.white,
            fontSize: "22px",
            fontWeight: "600",
            marginBottom: "6px",
          }}>
            {selectedCase.case_name || selectedCase.name || selectedCase.title || "Unnamed Case"}
          </h2>
          <div style={{color: colors.gray400, fontSize: "14px"}}>
            {selectedCase.case_number}
          </div>
        </div>

        <h3 style={{
          color: colors.teal300,
          fontSize: "14px",
          fontWeight: "600",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          marginBottom: "16px",
        }}>
          Documents ({files.length})
        </h3>

        {filesLoading ? (
          <div style={{textAlign: "center", padding: "40px"}}>
            <div style={{color: colors.gray400}}>Loading documents...</div>
          </div>
        ) : files.length > 0 ? (
          <div style={{
            backgroundColor: colors.navy700,
            borderRadius: "12px",
            border: `1px solid ${colors.teal700}`,
            overflow: "hidden",
          }}>
            {files.map((f, i) => {
              const docId = f.id;
              const isDownloading = downloading[docId] === true;
              const isDone = downloading[docId] === 'done';

              return (
                <div
                  key={f.id || i}
                  style={{
                    padding: "14px 16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "12px",
                    borderBottom: i < files.length - 1 ? `1px solid ${colors.teal700}` : "none",
                    transition: "background-color 0.15s",
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = colors.navy600}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                >
                  <div style={{display: "flex", alignItems: "center", gap: "12px", flex: 1, minWidth: 0}}>
                    <span style={{fontSize: "20px"}}>{getFileIcon(getFileName(f))}</span>
                    <span style={{color: colors.white, fontSize: "14px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
                      {getFileName(f)}
                    </span>
                  </div>

                  {isTauri && (
                    <button
                      onClick={(e) => { e.stopPropagation(); downloadFile(f); }}
                      disabled={isDownloading}
                      style={{
                        padding: "6px 12px",
                        backgroundColor: isDone ? colors.teal700 : "transparent",
                        border: `1px solid ${isDone ? colors.teal500 : colors.teal700}`,
                        color: isDone ? colors.teal300 : colors.teal400,
                        borderRadius: "4px",
                        cursor: isDownloading ? "not-allowed" : "pointer",
                        fontSize: "12px",
                        opacity: isDownloading ? 0.6 : 1,
                        minWidth: "80px",
                        textAlign: "center",
                      }}
                    >
                      {isDownloading ? "..." : isDone ? "Done" : "Download"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{
            backgroundColor: colors.navy700,
            borderRadius: "12px",
            padding: "40px",
            textAlign: "center",
            border: `1px solid ${colors.teal700}`,
          }}>
            <div style={{color: colors.gray400}}>No documents in this case</div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{padding: "24px"}}>
      <div style={{display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "24px"}}>
        <div>
          <h2 style={{
            color: colors.white,
            fontSize: "24px",
            fontWeight: "600",
            marginBottom: "4px",
          }}>
            Cases
          </h2>
          <div style={{color: colors.gray400, fontSize: "14px"}}>
            {cases.length} active case{cases.length !== 1 ? "s" : ""}
          </div>
        </div>

        {isTauri && syncBasePath && (
          <div style={{display: "flex", gap: "10px"}}>
            <button
              onClick={startPrecache}
              disabled={precaching}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 14px",
                backgroundColor: colors.navy700,
                border: `1px solid ${colors.gold400}`,
                color: colors.gold400,
                borderRadius: "6px",
                cursor: precaching ? "not-allowed" : "pointer",
                fontSize: "14px",
                opacity: precaching ? 0.6 : 1,
              }}
            >
              {precaching ? "Pre-caching..." : "Pre-cache for Calendar"}
            </button>
            <button
              onClick={openVirgoFolder}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 14px",
                backgroundColor: colors.navy700,
                border: `1px solid ${colors.teal700}`,
                color: colors.teal300,
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "14px",
              }}
            >
              Open ~/Virgo
            </button>
          </div>
        )}
      </div>

      {isTauri && precaching && precacheStatus && (
        <div style={{
          backgroundColor: colors.navy700,
          borderRadius: "8px",
          padding: "16px",
          marginBottom: "20px",
          border: `1px solid ${colors.gold400}`,
        }}>
          <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px"}}>
            <span style={{color: colors.gold400, fontWeight: "600", fontSize: "14px"}}>
              Pre-caching for upcoming calendar events...
            </span>
            <span style={{color: colors.gray400, fontSize: "13px"}}>
              {precacheStatus.cached_cases}/{precacheStatus.total_cases} cases
            </span>
          </div>
          {precacheStatus.current_case && (
            <div style={{color: colors.gray400, fontSize: "13px"}}>
              Current: {precacheStatus.current_case}
            </div>
          )}
          <div style={{
            marginTop: "8px",
            height: "4px",
            backgroundColor: colors.navy600,
            borderRadius: "2px",
            overflow: "hidden",
          }}>
            <div style={{
              width: precacheStatus.total_cases > 0
                ? `${(precacheStatus.cached_cases / precacheStatus.total_cases) * 100}%`
                : "0%",
              height: "100%",
              backgroundColor: colors.gold400,
              transition: "width 0.3s",
            }} />
          </div>
        </div>
      )}

      {cases.length > 0 ? (
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: "16px",
        }}>
          {cases.map((c) => (
            <div
              key={c.id}
              onClick={() => openCase(c)}
              style={{
                padding: "20px",
                backgroundColor: colors.navy700,
                border: `1px solid ${colors.teal700}`,
                borderRadius: "12px",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.borderColor = colors.teal500;
                e.currentTarget.style.transform = "translateY(-2px)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.borderColor = colors.teal700;
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              <div style={{
                color: colors.white,
                fontWeight: "600",
                fontSize: "16px",
                marginBottom: "8px",
              }}>
                {c.case_name || c.name || c.title || "Unnamed Case"}
              </div>
              <div style={{
                color: colors.gray400,
                fontSize: "13px",
              }}>
                {c.case_number}
              </div>
              {c.client_name && (
                <div style={{
                  color: colors.teal400,
                  fontSize: "13px",
                  marginTop: "8px",
                }}>
                  {c.client_name}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div style={{
          backgroundColor: colors.navy700,
          borderRadius: "12px",
          padding: "60px 20px",
          textAlign: "center",
          border: `1px solid ${colors.teal700}`,
        }}>
          <div style={{color: colors.gray400, marginBottom: "16px"}}>
            No active cases found
          </div>
          <button
            onClick={fetchCases}
            style={{
              padding: "10px 20px",
              backgroundColor: "transparent",
              border: `1px solid ${colors.teal500}`,
              color: colors.teal300,
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            Refresh
          </button>
        </div>
      )}
    </div>
  );
}
